/**
 * Core syntax evaluation utilities for marking correct/incorrect answers
 * Supports all three difficulty levels: Elementary, Intermediate, Advanced
 */

import { tagInfo, phraseInfo } from '../features/assignment/components/analysis/syntax_analysis/constants';

/**
 * Generate unique element ID for tracking individual syntax elements
 * @param {number[]} tokenIndices - Array of token indices for the element
 * @param {string} type - Element type (s, v, o, etc.)
 * @param {number} pageIndex - Page index for uniqueness
 * @returns {string} Unique element identifier
 */
export const generateElementId = (tokenIndices, type, pageIndex = 0) => {
  const tokenString = [...tokenIndices].sort((a, b) => a - b).join('-');
  return `element_${pageIndex}_${tokenString}_${type}`;
};

/**
 * Compare two token index arrays for equality
 * @param {number[]} indices1 - First array of token indices
 * @param {number[]} indices2 - Second array of token indices
 * @returns {boolean} True if arrays contain same indices
 */
export const compareTokenIndices = (indices1, indices2) => {
  if (!indices1 || !indices2 || indices1.length !== indices2.length) {
    return false;
  }
  
  const sorted1 = [...indices1].sort((a, b) => a - b);
  const sorted2 = [...indices2].sort((a, b) => a - b);
  
  return sorted1.every((index, i) => index === sorted2[i]);
};

/**
 * Find student answer that matches given token indices
 * @param {Array} studentData - Student's answer data
 * @param {number[]} targetIndices - Token indices to match
 * @returns {Object|null} Matching student answer or null
 */
export const findStudentAnswerByIndices = (studentData, targetIndices) => {
  for (const item of studentData) {
    if (item.type === 'group' && item.text) {
      const studentIndices = item.text.map(t => t.index);
      if (compareTokenIndices(studentIndices, targetIndices)) {
        return {
          type: item.groupType,
          tokenIndices: studentIndices,
          studentData: item
        };
      }
    }
  }
  return null;
};

/**
 * Evaluate Elementary level syntax answers
 * @param {Array} studentData - Student's grouped answer data
 * @param {Array} correctAnswers - Correct answer data from parsed_syntax
 * @param {number} pageIndex - Current page index for element ID generation
 * @returns {Object} Evaluation result with correctness details
 */
export const evaluateElementarySyntax = (studentData, correctAnswers, pageIndex = 0) => {
  const correctness = {};
  const elementDetails = [];
  let correctCount = 0;
  
  // Get all tag types that should be evaluated
  const allTagList = Object.keys(tagInfo);
  const evaluableAnswers = correctAnswers.filter(answer => allTagList.includes(answer.type));
  
  evaluableAnswers.forEach(correctAnswer => {
    const elementId = generateElementId(correctAnswer.token_indices, correctAnswer.type, pageIndex);
    const studentAnswer = findStudentAnswerByIndices(studentData, correctAnswer.token_indices);
    
    const isCorrect = studentAnswer && studentAnswer.type === correctAnswer.type;
    
    correctness[elementId] = {
      isCorrect,
      expectedType: correctAnswer.type,
      studentType: studentAnswer?.type || null,
      tokenIndices: correctAnswer.token_indices,
      elementId
    };
    
    elementDetails.push({
      id: elementId,
      element: tagInfo[correctAnswer.type]?.tagText || correctAnswer.type,
      isCorrect,
      expected: correctAnswer.type,
      actual: studentAnswer?.type || null
    });
    
    if (isCorrect) correctCount++;
  });
  
  return {
    correctness,
    elementDetails,
    isAllCorrect: correctCount === evaluableAnswers.length,
    correctCount,
    totalCount: evaluableAnswers.length,
    level: 'elementary'
  };
};

/**
 * Evaluate Intermediate level syntax answers
 * @param {Array} studentData - Student's answer data (mix of tokens and groups)
 * @param {Array} correctAnswers - Correct answer data from parsed_syntax
 * @param {number} pageIndex - Current page index for element ID generation
 * @returns {Object} Evaluation result with correctness details
 */
export const evaluateIntermediateSyntax = (studentData, correctAnswers, pageIndex = 0) => {
  const correctness = {};
  const elementDetails = [];
  let correctCount = 0;
  
  const allTagList = Object.keys(tagInfo);
  const evaluableAnswers = correctAnswers.filter(answer => allTagList.includes(answer.type));
  
  evaluableAnswers.forEach(correctAnswer => {
    const elementId = generateElementId(correctAnswer.token_indices, correctAnswer.type, pageIndex);
    const studentAnswer = findStudentAnswerByIndices(studentData, correctAnswer.token_indices);
    
    const isCorrect = studentAnswer && studentAnswer.type === correctAnswer.type;
    
    correctness[elementId] = {
      isCorrect,
      expectedType: correctAnswer.type,
      studentType: studentAnswer?.type || null,
      tokenIndices: correctAnswer.token_indices,
      elementId
    };
    
    elementDetails.push({
      id: elementId,
      element: tagInfo[correctAnswer.type]?.tagText || correctAnswer.type,
      isCorrect,
      expected: correctAnswer.type,
      actual: studentAnswer?.type || null
    });
    
    if (isCorrect) correctCount++;
  });
  
  return {
    correctness,
    elementDetails,
    isAllCorrect: correctCount === evaluableAnswers.length,
    correctCount,
    totalCount: evaluableAnswers.length,
    level: 'intermediate'
  };
};

/**
 * Evaluate Advanced level syntax answers (both groups and phrases)
 * @param {Object} studentGroupData - Student's group data
 * @param {Object} studentPhraseData - Student's phrase data
 * @param {Array} correctAnswers - Correct answer data from parsed_syntax
 * @param {number} pageIndex - Current page index for element ID generation
 * @returns {Object} Evaluation result with correctness details
 */
export const evaluateAdvancedSyntax = (studentGroupData, studentPhraseData, correctAnswers, pageIndex = 0) => {
  const correctness = {};
  const elementDetails = [];
  let correctCount = 0;
  
  const allTagList = Object.keys(tagInfo);
  const allPhraseList = Object.keys(phraseInfo);
  
  // Evaluate group elements (S, V, O, etc.)
  const groupAnswers = correctAnswers.filter(answer => allTagList.includes(answer.type));
  groupAnswers.forEach(correctAnswer => {
    const elementId = generateElementId(correctAnswer.token_indices, correctAnswer.type, pageIndex);
    
    // Check if student has correct group data for these token indices
    let isCorrect = true;
    let studentType = null;
    
    for (const tokenIndex of correctAnswer.token_indices) {
      const studentGroup = studentGroupData[tokenIndex];
      if (!studentGroup || studentGroup.groupType !== correctAnswer.type) {
        isCorrect = false;
        break;
      }
      studentType = studentGroup.groupType;
    }
    
    correctness[elementId] = {
      isCorrect,
      expectedType: correctAnswer.type,
      studentType,
      tokenIndices: correctAnswer.token_indices,
      elementId
    };
    
    elementDetails.push({
      id: elementId,
      element: tagInfo[correctAnswer.type]?.tagText || correctAnswer.type,
      isCorrect,
      expected: correctAnswer.type,
      actual: studentType
    });
    
    if (isCorrect) correctCount++;
  });
  
  // Evaluate phrase elements (전치사구, 부사구, etc.)
  const phraseAnswers = correctAnswers.filter(answer => allPhraseList.includes(answer.type));
  phraseAnswers.forEach(correctAnswer => {
    const elementId = generateElementId(correctAnswer.token_indices, correctAnswer.type, pageIndex);
    const startIndex = correctAnswer.token_indices[0];
    const endIndex = correctAnswer.token_indices[correctAnswer.token_indices.length - 1];
    
    // Check if student has correct phrase data
    let isCorrect = false;
    
    const studentStartPhrase = studentPhraseData[startIndex];
    const studentEndPhrase = studentPhraseData[endIndex];
    
    if (studentStartPhrase && studentEndPhrase) {
      // Check if phrase spans correctly
      const hasStartPhrase = studentStartPhrase.start?.some(phrase => 
        phrase.type === correctAnswer.type && phrase.endIndex === endIndex
      );
      const hasEndPhrase = studentEndPhrase.end?.some(phrase => 
        phrase.type === correctAnswer.type && phrase.startIndex === startIndex
      );
      
      isCorrect = hasStartPhrase && hasEndPhrase;
    }
    
    correctness[elementId] = {
      isCorrect,
      expectedType: correctAnswer.type,
      studentType: isCorrect ? correctAnswer.type : null,
      tokenIndices: correctAnswer.token_indices,
      elementId
    };
    
    elementDetails.push({
      id: elementId,
      element: phraseInfo[correctAnswer.type]?.tagText || correctAnswer.type,
      isCorrect,
      expected: correctAnswer.type,
      actual: isCorrect ? correctAnswer.type : null
    });
    
    if (isCorrect) correctCount++;
  });
  
  const totalAnswers = groupAnswers.length + phraseAnswers.length;
  
  return {
    correctness,
    elementDetails,
    isAllCorrect: correctCount === totalAnswers,
    correctCount,
    totalCount: totalAnswers,
    level: 'advanced'
  };
};

/**
 * Add correctness information to answer data structure
 * @param {Array} answers - Original answer array
 * @param {Object} correctnessData - Correctness evaluation results
 * @returns {Array} Enhanced answers with isCorrect and other tracking fields
 */
export const enhanceAnswersWithCorrectness = (answers, correctnessData) => {
  return answers.map(answer => {
    const elementId = generateElementId(answer.token_indices, answer.type);
    const correctnessInfo = correctnessData[elementId];
    
    return {
      ...answer,
      isCorrect: correctnessInfo?.isCorrect || false,
      studentAnswer: correctnessInfo?.studentType || null,
      elementId,
      lastEvaluated: new Date().toISOString()
    };
  });
};

/**
 * Create correctness tracking object for overall progress
 * @param {Object} evaluationResult - Result from syntax evaluation
 * @param {number} submitCount - Current submission count
 * @returns {Object} Correctness tracking data
 */
export const createCorrectnessTracking = (evaluationResult, submitCount = 1) => {
  return {
    totalElements: evaluationResult.totalCount,
    correctElements: evaluationResult.correctCount,
    incorrectElements: evaluationResult.totalCount - evaluationResult.correctCount,
    lastSubmitTime: new Date().toISOString(),
    submitCount,
    level: evaluationResult.level,
    isAllCorrect: evaluationResult.isAllCorrect
  };
};

/**
 * Get elements that are incorrect for retry system
 * @param {Object} correctnessData - Correctness evaluation results
 * @returns {Array} Array of incorrect element information
 */
export const getIncorrectElements = (correctnessData) => {
  return Object.values(correctnessData)
    .filter(element => !element.isCorrect)
    .map(element => ({
      elementId: element.elementId,
      expectedType: element.expectedType,
      studentType: element.studentType,
      tokenIndices: element.tokenIndices
    }));
};

/**
 * Check if maximum attempts reached and should auto-reveal answers
 * @param {number} submitCount - Current submission count
 * @param {string} level - Difficulty level (elementary, intermediate, advanced)
 * @returns {boolean} True if should auto-reveal
 */
export const shouldAutoRevealAnswers = (submitCount, level) => {
  const maxAttempts = {
    elementary: 3,
    intermediate: 3,
    advanced: 3
  };

  return submitCount >= maxAttempts[level];
};

/**
 * Create auto-revealed answer data when max attempts reached
 * @param {Array} correctAnswers - Correct answer data
 * @param {number} pageIndex - Current page index
 * @returns {Object} Auto-revealed correctness data
 */
export const createAutoRevealedAnswers = (correctAnswers, pageIndex = 0) => {
  const correctness = {};
  const elementDetails = [];
  
  correctAnswers.forEach(answer => {
    const elementId = generateElementId(answer.token_indices, answer.type, pageIndex);
    
    correctness[elementId] = {
      isCorrect: true, // Auto-revealed as correct for progression
      expectedType: answer.type,
      studentType: answer.type,
      tokenIndices: answer.token_indices,
      elementId,
      autoRevealed: true
    };
    
    const info = tagInfo[answer.type] || phraseInfo[answer.type];
    elementDetails.push({
      id: elementId,
      element: info?.tagText || answer.type,
      isCorrect: true,
      expected: answer.type,
      actual: answer.type,
      autoRevealed: true
    });
  });
  
  return {
    correctness,
    elementDetails,
    isAllCorrect: true,
    correctCount: correctAnswers.length,
    totalCount: correctAnswers.length,
    autoRevealed: true
  };
};